import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';

import { company } from '~/company/Company';
import { formatVolume } from '~/components/ProductCard/utils';
import { useProductCardV3 } from '~/components/ProductCardV3/hooks/useProductCardV3';
import {
  AddToCartSection,
  ProductBadges,
  ProductImage,
  ProductInfo,
} from '~/components/ProductCardV3/shared';
import type { ProductCardVariantProps } from '~/components/ProductCardV3/types';
import { useProductSnippetAnalytics } from '~/components/ProductListAnalytics';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import { calcDiscountPercentage } from '~/utils/formaters';

import PriceSection from '../../shared/PriceSection/PriceSection';
import styles from './ProductCardXL.module.scss';

/**
 * ProductCardXL - Former large card size (338px web)
 */
const ProductCardXL = observer(
  ({
    product,
    index,
    className,
    showColorVariants = false,
    productState,
    disabled = false,
  }: ProductCardVariantProps) => {
    const { handleClickProduct } = useCategoryAnalytics();

    const {
      currentItem,
      offerSku,
      image,
      oldPrice,
      currentPrice,
      totalCartPrice,
      cartItemsCount,
      isFavorite,
      isOutOfStock,
      colorVariants,
      badges,
      multiBuyInfo,
      mixAndMatch,
      limit,
      productHasManyVariants,
      isBundle,
      selectOffer,
      toggleFavorite,
      changeCount,
      openSelectSKUModal,
    } = useProductCardV3(
      productState?.data ?? product,
      productState?.selectedOffer,
      'xl',
    );

    const { snippetRef, trackSnippetClick } = useProductSnippetAnalytics(
      currentItem,
      index,
    );

    const handleProductClick = useCallback(() => {
      trackSnippetClick();
      handleClickProduct(product);
    }, [product, handleClickProduct, trackSnippetClick]);

    const volume = formatVolume(currentItem.properties);
    const hasDiscount = oldPrice && oldPrice !== currentPrice;
    const imageSrc = currentItem.images[0] || image;
    const discountPercent =
      currentItem.discountPrice &&
      currentItem.price &&
      currentItem.discountPrice < currentItem.price
        ? calcDiscountPercentage(currentItem.price, currentItem.discountPrice)
        : 0;
    const discountLabel =
      discountPercent > 0 ? `-${discountPercent}%` : undefined;

    return (
      <div
        ref={snippetRef}
        className={classes([
          styles.card,
          disabled && styles.disabled,
          className,
        ])}
        data-company={company.name}
      >
        <ProductImage
          src={imageSrc}
          alt={currentItem.name}
          size="xl"
          badges={<ProductBadges badges={badges} size="m" variant="brand" />}
          isFavorite={isFavorite}
          onToggleFavorite={toggleFavorite}
          productSlug={product.slug}
          offerSku={offerSku}
          onProductClick={handleProductClick}
          cartItemsCount={cartItemsCount}
          totalCartPrice={totalCartPrice}
          isOutOfStock={isOutOfStock}
        />

        <div className={styles.infoWrapper}>
          <ProductInfo
            onProductClick={handleProductClick}
            name={currentItem.name}
            volume={volume}
            rating={currentItem.ratingAverage}
            isRatingShown
            size="l"
            colorVariants={
              showColorVariants && colorVariants.length > 1
                ? colorVariants.map((v) => ({
                    color: v.value,
                    sku: v.sku,
                    name: v.value,
                    image: v.image,
                  }))
                : undefined
            }
            onVariantSelect={selectOffer}
            productSlug={product.slug}
            currentSku={offerSku}
            multiBuyInfo={multiBuyInfo}
            cartItemsCount={cartItemsCount}
            promoRequiredQuantity={currentItem.promoRequiredQuantity}
          />
        </div>
        <PriceSection
          currentPrice={mixAndMatch?.priceFrom ?? currentPrice}
          oldPrice={
            mixAndMatch
              ? mixAndMatch.regularPrice
              : hasDiscount
                ? oldPrice
                : undefined
          }
          discount={mixAndMatch || !hasDiscount ? undefined : discountLabel}
          size="l"
          // Out of stock: hide the price so the "Notify me" button spans the row.
          hidePrice={isOutOfStock}
          action={
            <AddToCartSection
              currentPrice={currentPrice}
              cartItemsCount={cartItemsCount}
              limit={limit}
              size="m"
              onChangeCount={changeCount}
              productId={product?.id ?? 0}
              sku={currentItem.sku ?? ''}
              isOutOfStock={isOutOfStock}
              productSlug={product.slug}
              productHasManyVariants={productHasManyVariants}
              isBundle={isBundle}
              onOpenSelectSKUModal={openSelectSKUModal}
              hidePrice
            />
          }
        />
        {disabled && <div className={styles.disabledOverlay} />}
      </div>
    );
  },
);

export default ProductCardXL;
